import { collection, query, where, addDoc, getDocs, onSnapshot, serverTimestamp, orderBy, writeBatch } from "firebase/firestore";
import { db } from "../config/firebase"

/**
 * Creates a chat room if one does not already exist between the specified patient and doctor.
 * @param {string} patientID - The ID of the patient.
 * @param {string} doctorID - The ID of the doctor.
 * @returns {Promise<string>} - The chat room ID.
 */
export async function createChatRoom(patientID, doctorID) {
    const chatsRef = collection(db, "chats");

    // Check if a chat already exists between these users
    const q = query(chatsRef, where("users", "array-contains", patientID));
    const chatSnapshot = await getDocs(q);

    const existingChat = chatSnapshot.docs.find(doc =>
        doc.data().users.includes(doctorID)
    );

    if (existingChat) {
        return existingChat.id; // Return existing chat room ID
    }

    // Create a new chat room
    const newChat = await addDoc(chatsRef, {
        users: [patientID, doctorID],
        createdAt: new Date(),
    });

    return newChat.id; // Return new chat room ID
}

export function getChatList(userID, setChatList) {
    const chatsRef = collection(db, "chats");
    const q = query(chatsRef, where("users", "array-contains", userID));

    onSnapshot(q, (snapshot) => {
        const chats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setChatList(chats);
    });
}

export async function sendMessage(senderID, recipientID, text) {
    const messagesRef = collection(db, "messages");
    await addDoc(messagesRef, {
        senderID,
        recipientID,
        text,
        timestamp: serverTimestamp(),
        seen: false,
    });
}

export function listenForMessages(user1ID, user2ID, setMessages) {
    const messagesRef = collection(db, "messages");
    const q = query(
        messagesRef,
        where("senderID", "in", [user1ID, user2ID]),
        where("recipientID", "in", [user1ID, user2ID]),
        orderBy("timestamp")
    );

    return onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMessages(messages);
    });
}

export async function markMessagesAsSeen(recipientID, senderID) {
    const messagesRef = collection(db, "messages");

    // Query for messages where the recipient is the current user and `seen` is false
    const q = query(
        messagesRef,
        where("recipientID", "==", recipientID),
        where("senderID", "==", senderID),
        where("seen", "==", false)
    );

    const querySnapshot = await getDocs(q);
    const batch = writeBatch(db);

    // Add all messages to the batch
    querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { seen: true });
    });

    // Commit the batch
    await batch.commit();
}

export async function countUnreadMessages(userID) {
    const messagesRef = collection(db, "messages");

    // Query for messages where the recipient is the user and `seen` is false
    const q = query(
        messagesRef,
        where("recipientID", "==", userID),
        where("seen", "==", false)
    );

    const querySnapshot = await getDocs(q);

    // Return the count of unread messages
    return querySnapshot.size;
}

export async function countUnreadMessagesByDoctor(userID) {
    const messagesRef = collection(db, "messages");

    // Query for unread messages where the user is the recipient
    const q = query(
        messagesRef,
        where("recipientID", "==", userID),
        where("seen", "==", false)
    );

    const querySnapshot = await getDocs(q);

    // Create a map to store unread message counts per doctor
    const unreadCounts = {};

    querySnapshot.forEach((doc) => {
        const senderID = doc.data().senderID;
        unreadCounts[senderID] = (unreadCounts[senderID] || 0) + 1;
    });

    return unreadCounts; // Returns an object: { doctorID1: count, doctorID2: count, ... }
}

export function listenForPatientsList(userID, setPatientsList) {
    if (!userID) return;
  
    const messagesRef = collection(db, "messages");
  
    const q = query(
      messagesRef,
      where("senderID", "==", userID),
    );
  
    const q2 = query(
      messagesRef,
      where("recipientID", "==", userID),
    );
  
    const unsubscribe1 = onSnapshot(q, (snapshot) => updatePatientList(snapshot, setPatientsList));
    const unsubscribe2 = onSnapshot(q2, (snapshot) => updatePatientList(snapshot, setPatientsList));
  
    function updatePatientList(snapshot, setPatientsList) {
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const patientID = data.senderID === userID ? data.recipientID  : data.senderID;
        setPatientsList(prev => [...new Set([...prev, patientID])]); 
      });
    }
  
    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }
  